import type { UnwrapRef } from "vue"

import type { BaseSchema, InferOutput, SafeParseResult } from "valibot"
import { getDefault, getFallback, safeParse, unwrap } from "valibot"

export type UseFieldProps<TSchema extends BaseSchema<any, any, any>> = {
  name: string
  schema: MaybeRef<TSchema>
  defaultValue?: MaybeRef<InferOutput<TSchema>>
}

/**
 * Creates a Valibot field composable.
 * @param fieldName - The name of the field.
 * @param schema - The schema for the field.
 * @returns An object containing various properties and functions related to the Valibot field.
 */
export function useField<TSchema extends BaseSchema<any, any, any>>(
  props: UseFieldProps<TSchema>
): UseFieldReturnType<TSchema> {
  const { name: fieldName, schema: originalSchema, defaultValue = ref(null) } = toRefs(reactive(props))
  const schemaUnwrapped = computed((): TSchema extends { wrapped: infer U } ? U : TSchema =>
    "wrapped" in originalSchema.value ? unwrap(originalSchema.value) : originalSchema.value
  )
  const state = ref<InferOutput<TSchema>>(
    (defaultValue as Ref)?.value ??
      getDefault(originalSchema.value) ??
      (getFallback(originalSchema.value) as InferOutput<TSchema>)
  )
  const userErrors = ref<string[]>([])
  const dirty = ref(false)
  const touched = ref(false)
  const defaultParseResult = safeParse(originalSchema.value, state.value)
  const parseResult = ref<Pick<SafeParseResult<TSchema>, "issues" | "success">>({
    success: defaultParseResult.success,
    issues: [] as any,
  })

  const isValid = computed((): boolean => parseResult.value.success)
  const eagerValid = computed(() => {
    const result = safeParse(originalSchema.value, state.value)
    return result.success
  })

  const errors = computed((): string[] => {
    return userErrors.value.concat(parseResult.value.issues?.map((issue) => issue.message as string) || [])
  })

  const errorMessage = computed((): string => {
    return errors.value?.[0]
  })

  const binds = computed(() => {
    return {
      name: fieldName?.value,
      modelValue: state.value,
      "onUpdate:modelValue": (value: any) => {
        state.value = value
        if (!dirty.value && (value !== 0 || value !== "")) dirty.value = true

        clearErrors()
      },
      onBlur: () => {
        validate()
      },
      onFocus: () => {
        touched.value = true
      },
    }
  })

  function fixValue(value: any): any {
    if (value === "" || value === undefined) return null
    return value
  }

  function validate(): boolean {
    const result = safeParse(originalSchema.value, fixValue(state.value))

    parseResult.value = {
      success: result.success,
      issues: result.issues,
    }

    return result.success
  }

  function reset(): void {
    state.value = null as InferOutput<TSchema>
    dirty.value = false
    touched.value = false
    clearErrors()
  }

  function setErrors(...errors: string[]): void {
    userErrors.value = errors
  }

  function clearErrors(): void {
    userErrors.value = []
    parseResult.value = {
      success: defaultParseResult.success,
      issues: [] as any,
    }
  }

  function setState(value: InferOutput<TSchema>, validateState = true): void {
    state.value = value

    clearErrors()
    if (validateState) validate()
  }

  return {
    type: "field",
    fieldName: fieldName.value,

    state,
    dirty,
    touched,
    parseResult,
    isValid,
    eagerValid,
    errors,
    errorMessage,
    binds,
    validate,
    reset,
    setErrors,
    clearErrors,
    setState,
  }
}

/**
 * Represents the return type of the `useForm` composable function.
 * It provides various properties and methods related to form fields.
 *
 * @template T - The type of the form field schema.
 */
export type UseFieldReturnType<T extends BaseSchema<any, any, any>> = {
  /**
   * The type of the form.
   */
  type: "field"

  /**
   * Field name
   */
  fieldName: string

  /**
   * The state of the form field.
   */
  state: Ref<UnwrapRef<InferOutput<T>>>

  /**
   * A reactive reference to the dirty state of the form field.
   */
  dirty: Ref<boolean>

  /**
   * A reactive reference to the touched state of the form field.
   */
  touched: Ref<boolean>

  /**
   * The validation result of the form field.
   */
  parseResult: Ref<Pick<SafeParseResult<T>, "issues" | "success">>

  /**
   * A reactive reference to the validity of the form field.
   */
  isValid: ComputedRef<boolean>

  /**
   * A reactive reference to the eager validity of the form field.
   * It does not wait until the field is touched.
   */
  eagerValid: ComputedRef<boolean>

  /**
   * A reactive reference to the error message of the form field.
   */
  errorMessage: ComputedRef<string>

  /**
   * A reactive reference to the errors of the form field.
   */
  errors: ComputedRef<string[]>

  /**
   * A reactive reference to the bindings of the form field.
   */
  binds: ComputedRef<{
    name: string
    modelValue: any
    "onUpdate:modelValue": (value: any) => void
    onBlur: () => void
  }>

  /**
   * Validates the form field.
   *
   * @returns A boolean indicating whether the form field is valid.
   */
  validate: () => boolean

  /**
   * Resets the form field.
   */
  reset: () => void

  /**
   * Sets the errors of the form field.
   *
   * @param errors - The errors to set.
   */
  setErrors: (...errors: string[]) => void

  /**
   * Clears the errors of the form field.
   */
  clearErrors: () => void

  /**
   * Sets the state of the form field.
   */
  setState: (value: InferOutput<T>, validateState?: boolean) => void
}
